// Scanner device mode — persists per-browser via localStorage.
//
// 'personal' (default): the Firefleet-authenticated user IS the operator. No
//                       badge+PIN gate; audit identity = JWT user.
// 'shared'           : a fixed shop-floor device. Operators must badge+PIN to
//                       start a session; audit identity = the badged user.
//
// Defaulting to 'personal' is the deliberate trade-off: most ops devices are
// phones or laptops, and forcing every personal user through a badge gate would
// be hostile. Shared scanners must be configured explicitly by an admin (see
// Admin/ScannerDeviceConfig.tsx).

import { useState, useEffect, useCallback } from 'react';
import { inShell } from './shell';

export type DeviceMode = 'personal' | 'shared';

const STORAGE_KEY = 'firefleetScannerMode';
const VALID_MODES: ReadonlyArray<DeviceMode> = ['personal', 'shared'];

function readStoredMode(): DeviceMode {
  // The kiosk shell is a shared device by definition, whatever localStorage says.
  if (inShell()) return 'shared';
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && (VALID_MODES as readonly string[]).includes(raw)) {
      return raw as DeviceMode;
    }
  } catch {
    // localStorage unavailable (private mode etc.) — fall through to default.
  }
  return 'personal';
}

/**
 * Read + update scanner device mode for THIS browser.
 *
 * Returns a stable tuple of [mode, setMode]. setMode persists to localStorage
 * and broadcasts via the `storage` event so other tabs in the same browser see
 * the change immediately.
 */
export function useDeviceMode(): [DeviceMode, (next: DeviceMode) => void] {
  const [mode, setMode] = useState<DeviceMode>(readStoredMode);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setMode(readStoredMode());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const updateMode = useCallback((next: DeviceMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Best effort — if localStorage rejects, still update the in-memory state
      // so the current tab sees the change.
    }
    setMode(next);
  }, []);

  return [mode, updateMode];
}
